import type { Theme } from '@mui/material';
import {
  Box,
  Button,
  Card,
  CardHeader,
  Divider,
  Grid,
  Typography,
  useMediaQuery,
} from '@mui/material';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { UpdateDestinationDirectionTipsDialog } from '../../organisms/dialogs/update-destination-direction-tips-dialog/update-destination-direction-tips-dialog';

import { DestinationDirectionTips as DestinationDirectionTipsType } from '@/features/destinations/api/types/destination';
import { useDialog } from '@/hooks/use-dialog';

interface DestinationDirectionTipsProps {
  id: number;
  directionTips?: DestinationDirectionTipsType[];
}

export const DestinationDirectionTipsCard: FC<DestinationDirectionTipsProps> = props => {
  const { id, directionTips, ...other } = props;
  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const { t } = useTranslation(['destination', 'common']);
  const align = mdDown ? 'vertical' : 'horizontal';

  const [
    isOpenUpdateDestinationDirectionTipsDialog,
    handleOpenUpdateDestinationDirectionTipsDialog,
    handleCloseUpdateDestinationDirectionTipsDialog,
  ] = useDialog();

  const tipsByLanguage = useMemo(
    () => ({
      pl: directionTips?.find(item => item.language === 'pl')?.tips.map(tip => tip.content) || [],
      en: directionTips?.find(item => item.language === 'en')?.tips.map(tip => tip.content) || [],
      ua: directionTips?.find(item => item.language === 'ua')?.tips.map(tip => tip.content) || [],
    }),
    [directionTips],
  );

  const hasDirectionTips =
    tipsByLanguage.pl.length > 0 || tipsByLanguage.en.length > 0 || tipsByLanguage.ua.length > 0;
  const maxRows = Math.max(
    tipsByLanguage.pl.length,
    tipsByLanguage.en.length,
    tipsByLanguage.ua.length,
  );
  const rows = Array.from({ length: maxRows }, (_, index) => ({
    pl: tipsByLanguage.pl[index] || '-',
    en: tipsByLanguage.en[index] || '-',
    ua: tipsByLanguage.ua[index] || '-',
  }));

  return (
    <>
      <Card variant="outlined" {...other}>
        <CardHeader
          title={t('destination:directionTips.cardTitle')}
          action={
            <Button
              onClick={handleOpenUpdateDestinationDirectionTipsDialog}
              color="primary"
              variant="text"
              size="large">
              {t('common:button.edit')}
            </Button>
          }
        />
        <Divider />
        <Box sx={{ p: 3 }}>
          {!hasDirectionTips ? (
            <Typography variant="inherit">
              {t('destination:directionTips.noDirectionTips')}
            </Typography>
          ) : (
            <Grid container spacing={2}>
              <Grid item md={0.8} xs={12}>
                <Typography variant="subtitle2">#</Typography>
              </Grid>
              <Grid item md={3.7} xs={12}>
                <Typography variant="subtitle2">{t('destination:translations.polish')}</Typography>
              </Grid>
              <Grid item md={3.7} xs={12}>
                <Typography variant="subtitle2">{t('destination:translations.english')}</Typography>
              </Grid>
              <Grid item md={3.7} xs={12}>
                <Typography variant="subtitle2">
                  {t('destination:translations.ukrainian')}
                </Typography>
              </Grid>

              {rows.map((row, index) => (
                <Grid container item spacing={2} key={index}>
                  <Grid item md={0.8} xs={12}>
                    <Typography color="text.secondary" variant="body2">
                      {index + 1}
                    </Typography>
                  </Grid>
                  <Grid item md={3.7} xs={12}>
                    <Typography color="text.secondary" variant="body2">
                      {row.pl}
                    </Typography>
                  </Grid>
                  <Grid item md={3.7} xs={12}>
                    <Typography color="text.secondary" variant="body2">
                      {row.en}
                    </Typography>
                  </Grid>
                  <Grid item md={3.7} xs={12}>
                    <Typography color="text.secondary" variant="body2">
                      {row.ua}
                    </Typography>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Card>

      <UpdateDestinationDirectionTipsDialog
        open={isOpenUpdateDestinationDirectionTipsDialog}
        onCancel={handleCloseUpdateDestinationDirectionTipsDialog}
        destinationId={id}
        directionTips={directionTips}
      />
    </>
  );
};
